<script lang="ts">
  import Prose from '../lib/components/Prose.svelte';
  import FlamePanel from '../lib/algorithms/thinking-traces/FlamePanel.svelte';
  import { ANNOTATED_TRACES } from '../lib/data/annotated-traces';

  // Four traces already annotated in annotated-traces.ts. Each illustrates a
  // different point in the grind-vs-quit spectrum. Data: no new extraction.
  const haiku = ANNOTATED_TRACES.find((t) => t.name === 'haiku-d10-p0.conv');
  const sonnet = ANNOTATED_TRACES.find((t) => t.name === 'sonnet-d45-3.conv');
  const opusCommit = ANNOTATED_TRACES.find((t) => t.name === 'opus-d50-4.conv');
  const opusQuit = ANNOTATED_TRACES.find((t) => t.name === 'opus-d55-5.conv');
</script>

<section id="grind-vs-quit" class="section">
  <Prose>
    <header class="header">
      <div class="eyebrow">Behavior</div>
      <h2>How failure spends time</h2>
      <p class="summary">
        Near the capability boundary, models differ not only in accuracy, but in
        how long they keep trying after success is unlikely.
      </p>
    </header>
    <p>
      Pass rate is the headline metric. It leaves out the operational story: a
      five-minute grind, a nine-second refusal, a twenty-minute committed
      attempt. For a deployed agent, that difference matters. A model that burns
      fifteen minutes on an impossible task is very different from one that
      declines quickly and fails cheaply. These traces use the same task family,
      long multiplication, across three models near their own ceilings.
    </p>

    <h3 class="subhead">Haiku: at the edge</h3>
    <p>
      At 10 digits, Haiku is right on the boundary. It commits, works through
      partial products and carries for about a minute, and gets the answer
      right. A few digits larger, the same behavior would likely fail. Haiku
      does not appear to know it is near the edge; it simply runs the algorithm.
    </p>
  </Prose>

  {#if haiku}
    <div class="panel-slot">
      <FlamePanel
        trace={haiku}
        titleOverride="10-digit × 10-digit multiplication"
        outcome={{ label: 'correct · 13K tokens · 1m 6s', tone: 'ok' }}
      />
    </div>
  {/if}

  <Prose>
    <h3 class="subhead">Sonnet: the long grind</h3>
    <p>
      At 45 digits, Sonnet is past the size where it can reliably compute every
      step, but it keeps going anyway. The trace runs past sixty thousand output
      tokens and ten minutes of work, layering decomposition strategies on top
      of more decomposition. By the time the final answer appears, the
      operational cost has already been paid.
    </p>
  </Prose>

  {#if sonnet}
    <div class="panel-slot">
      <FlamePanel
        trace={sonnet}
        titleOverride="45-digit × 45-digit multiplication"
        outcome={{ label: 'completed · 62K tokens · 10m 43s grind', tone: 'neutral' }}
        showLegend={false}
      />
    </div>
  {/if}

  <Prose>
    <h3 class="subhead">Opus: commit or decline</h3>
    <p>
      Opus shows a sharper switch. Just below its ceiling, at 50 digits, it
      commits fully: eighty-three thousand tokens, nineteen minutes,
      Karatsuba-style decomposition, and verification passes folded into the
      run. Five digits later, at 55 digits, the behavior changes completely. The
      model judges the task as not worth attempting and returns a
      three-hundred-token refusal in nine seconds. Same model, same task type,
      same day; different behavior because feasibility crossed a learned
      threshold.
    </p>
  </Prose>

  {#if opusCommit}
    <div class="panel-slot">
      <FlamePanel
        trace={opusCommit}
        titleOverride="50-digit × 50-digit multiplication"
        outcome={{ label: 'correct · 83K tokens · 19m 27s commit', tone: 'ok' }}
        showLegend={false}
      />
    </div>
  {/if}

  {#if opusQuit}
    <div class="panel-slot">
      <FlamePanel
        trace={opusQuit}
        titleOverride="55-digit × 55-digit multiplication"
        outcome={{ label: 'declined · 333 tokens · 9s', tone: 'err' }}
        showLegend={false}
        maxChartHeight={180}
      />
    </div>
  {/if}

  <Prose>
    <p>
      That refusal is itself a learned generation pattern. One possible
      continuation is "try the algorithm"; another is "this is not worth
      attempting." From the outside, I cannot tell whether the behavior came
      from explicit examples or reward shaping on long trajectories. Either way,
      Opus learned a boundary that Sonnet did not.
    </p>
    <p>
      For deployed agents, the practical metric is not just pass rate. The
      refusal curve matters too. Two models with the same 60% accuracy can have
      very different production behavior if one spends eighty-three thousand
      tokens per attempt while the other exits cheaply when the task is too
      hard. The decision to try is not a separate control system; it is another
      generated step, subject to the same learned reliability as the rest of the
      trace.
    </p>
  </Prose>
</section>

<style>
  .section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: var(--space-4xl);
    width: 100%;
  }
  .header {
    margin-bottom: var(--space-lg);
  }
  .eyebrow {
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium);
    color: var(--ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: var(--space-sm);
  }
  .summary {
    font-family: var(--font-serif);
    font-style: italic;
    font-size: var(--text-lg);
    color: var(--ink-muted);
    line-height: var(--leading-snug);
    margin-top: var(--space-sm);
    margin-bottom: var(--space-lg);
  }
  :global(.prose) h3.subhead {
    font-family: var(--font-sans);
    font-size: var(--text-base);
    font-weight: var(--weight-medium);
    color: var(--ink);
    margin-top: var(--space-xl);
    margin-bottom: var(--space-sm);
  }
  .panel-slot {
    width: 100%;
    max-width: 880px;
    margin: var(--space-md) 0 var(--space-lg);
  }
</style>
